import { cospReference, etaSuggestion, previousFlowReport, voyageDistanceRun } from '@felix/domain';
import { Button } from '@mantine/core';
import { IconClockHour4, IconFlag, IconMapPin, IconRoute } from '@tabler/icons-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatHours } from '../../format.js';
import { ltUtcText } from '../../inputs/lt-utc.js';
import dep from './DepartureSections.module.css';
import { PortField, TextField } from './fields.js';
import classes from './NoonReportScreen.module.css';
import { Group, LocalDateTimeField, SECTION_ICON, SectionTitle } from './NoonSections.js';
import { formatLatitude, formatLongitude } from './noon-report.js';
import { useNumber } from './numbers.js';
import type { VoyageReport } from './types.js';

/**
 * Noon'un referans paneli (ADR 0059 §5; ortağın `g3.jpeg` §2 COSP Reference, §3 Previous Report,
 * §7 ETA / Port Information): COSP'tan beri süre, mesafe ve ortalama hız; önceki rapor ve geçen
 * süre; sonraki liman, Chart Datum / Area, ETA, toplam ve kalan mesafe. Hesaplar türetilir,
 * saklanmaz; liman, chart area ve ETA raporun isteğe bağlı alanlarıdır.
 */
export function NoonReferenceCard({
  reports,
  currentId,
  averageSpeedKn,
  readOnly,
}: {
  reports: readonly VoyageReport[];
  currentId: string | null;
  /** Bu raporun ortalama hızı (son rapordan beri); ETA önerisi COSP ortalamasıyla, yoksa bununla. */
  averageSpeedKn: number | null;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.noon.reference.${key}`, options ?? {});
  const { setValue } = useFormContext();
  const [reportedAt, offset, distance, toGo] = useWatch({
    name: [
      'reportedAt',
      'tzOffsetMin',
      'data.navigation.distanceSinceLast',
      'data.navigation.distanceToGo',
    ],
  }) as [string | undefined, number | undefined, unknown, unknown];
  if (!reportedAt) return null;
  const others = reports.filter((r) => r.id !== currentId && r.status === 'submitted');
  const current = {
    reportedAt,
    distanceNm: typeof distance === 'number' ? distance : null,
  };
  const cosp = cospReference(others, current);
  const previous = previousFlowReport(others, reportedAt);
  const run = voyageDistanceRun(others, current);
  const remaining = typeof toGo === 'number' ? toGo : null;
  const speed = cosp?.avgSpeedKn ?? averageSpeedKn;
  const eta = etaSuggestion(reportedAt, remaining, speed);
  const one = { digits: 1, fixed: true } as const;
  return (
    <section className={classes.card} aria-label={k('title')}>
      <SectionTitle icon={<IconRoute size={SECTION_ICON} />} title={k('title')} />
      <div className={classes.refGrid}>
        <Group title={k('cosp.title')} icon={<IconFlag size={18} />}>
          {cosp ? (
            <dl className={dep.facts}>
              <div>
                <dt>{k('cosp.time')}</dt>
                <dd>{ltUtcText(cosp.cosp.reportedAt, cosp.cosp.tzOffsetMin)}</dd>
              </div>
              <div>
                <dt>{k('cosp.position')}</dt>
                <dd>
                  {cosp.cosp.lat !== null && cosp.cosp.lon !== null
                    ? `${formatLatitude(cosp.cosp.lat)} ${formatLongitude(cosp.cosp.lon)}`
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>{k('cosp.elapsed')}</dt>
                <dd>{n.unit(cosp.hours, 'h', one)}</dd>
              </div>
              <div>
                <dt>{k('cosp.distance')}</dt>
                <dd>{n.unit(cosp.distanceNm, 'NM', one)}</dd>
              </div>
              <div>
                <dt>{k('cosp.avgSpeed')}</dt>
                <dd>{n.unit(cosp.avgSpeedKn, 'kn', { digits: 2, fixed: true })}</dd>
              </div>
            </dl>
          ) : (
            <p className={classes.muted}>{k('cosp.none')}</p>
          )}
        </Group>
        <Group title={k('previous.title')} icon={<IconClockHour4 size={18} />}>
          {previous ? (
            <dl className={dep.facts}>
              <div>
                <dt>{k('previous.type')}</dt>
                <dd>{t(`voyage.kinds.${previous.kind}`)}</dd>
              </div>
              <div>
                <dt>{k('previous.time')}</dt>
                <dd>{ltUtcText(previous.reportedAt, previous.tzOffsetMin)}</dd>
              </div>
              <div>
                <dt>{k('previous.elapsed')}</dt>
                <dd>{formatHours(previous.hours)}</dd>
              </div>
            </dl>
          ) : (
            <p className={classes.muted}>{k('previous.none')}</p>
          )}
        </Group>
        <Group title={k('eta.title')} icon={<IconMapPin size={18} />}>
          <div className={classes.refFields}>
            <PortField
              name="data.navigation.nextPort"
              codeName="data.navigation.nextPortCode"
              label={k('eta.nextPort')}
              readOnly={readOnly}
            />
            <TextField
              name="data.navigation.chartArea"
              label={k('eta.chartArea')}
              readOnly={readOnly}
            />
            <LocalDateTimeField
              name="data.navigation.eta"
              label={k('eta.eta')}
              readOnly={readOnly}
            />
          </div>
          <dl className={dep.facts}>
            <div>
              <dt>{k('eta.total')}</dt>
              <dd>{remaining === null ? '—' : n.unit(run + remaining, 'NM', one)}</dd>
            </div>
            <div>
              <dt>{k('eta.run')}</dt>
              <dd>{n.unit(run, 'NM', one)}</dd>
            </div>
            <div>
              <dt>{k('eta.remaining')}</dt>
              <dd>{n.unit(remaining, 'NM', one)}</dd>
            </div>
            <div>
              <dt>{k('eta.suggested', { speed: n(speed, { digits: 1, fixed: true }) })}</dt>
              <dd>{eta ? ltUtcText(eta, offset ?? 0) : '—'}</dd>
            </div>
          </dl>
          {!readOnly && eta ? (
            <Button
              size="xs"
              variant="light"
              className={classes.addButton}
              onClick={() => setValue('data.navigation.eta', eta, { shouldDirty: true })}
            >
              {k('eta.apply')}
            </Button>
          ) : null}
        </Group>
      </div>
    </section>
  );
}
